import React, { memo, useEffect, useMemo, useRef, useState } from 'react'
import { useCallback } from 'react'

import * as RadixAvatar from '@radix-ui/react-avatar'
import { InfiniteData } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { useTheme } from 'next-themes'
import randomcolor from 'randomcolor'
import { HiOutlineUser, HiUser } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import { AvatarSkeleton } from '@/components/common/Skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'

import { getFaviconUrl } from '@/helpers/url'

import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import {
  useOrganizationUser,
  useOrganizationUsers,
} from '@/hooks/organization/useOrganizationUsers'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { User, UserID } from '@/types/User'

export interface UsersProps {
  userIds: UUID[]
  direction?: 'ltr' | 'rtl'
  width?: number
  height?: number
  className?: string
  avatarClassName?: string
  animationDisabled?: boolean
  limit?: number
  onClick?: (user: User) => void
  hideOverflowBadge?: boolean
}

interface Avatar {
  src: string | undefined
  name: string | undefined
  width: number
  height: number
  noTooltip?: boolean
  includeLabel?: boolean
  align?: 'start' | 'center' | 'end'
  alignOffset?: number
  side?: 'top' | 'right' | 'bottom' | 'left'
  sideOffset?: number
  fallback?: string
  className?: string
  triggerClassName?: string
  labelClassName?: string
  delayFallback?: number
  initialShowFallback?: boolean
  startWithFallback?: boolean
  style?: React.CSSProperties
  onClick?: () => void
}

interface UserAvatarProps extends Omit<Avatar, 'src' | 'name'> {
  userId: UserID
  // Caller in a cross-org context should pass the user's org; default active org doesn't hold members from other orgs.
  orgId?: UUID | ''
  // Skip the fetch entirely when caller already has the resolved User.
  user?: User
}

interface CountAvatarProps {
  width: number
  height: number
  count: number
  className?: string
  users?: User[]
}

const getInitials = (name: string | undefined): string => {
  if (!name) {
    return '?'
  }
  const names = name.split(' ')
  let initials = names[0].substring(0, 1).toUpperCase()

  if (names.length > 1) {
    // Find the last word that starts with an alpha character
    for (let i = names.length - 1; i >= 1; i--) {
      const firstChar = names[i].substring(0, 1)
      if (/[a-zA-Z]/.test(firstChar)) {
        initials += firstChar.toUpperCase()
        break
      }
    }
  }

  return initials
}

interface AvatarImageProps {
  src: string
  alt: string | undefined
  width: number
  height: number
  onError?: () => void
  onLoad?: () => void
  className?: string
  style?: React.CSSProperties
}

// Memoized image component that only re-renders when src changes
const AvatarImage: React.FC<AvatarImageProps> = memo(
  ({ src, alt, width, height, onError, onLoad, className, style }) => {
    const imgRef = useRef<HTMLImageElement>(null)

    const handleError = useCallback(() => {
      onError?.()
    }, [onError])

    const handleLoad = useCallback(() => {
      onLoad?.()
    }, [onLoad])

    return (
      <img
        ref={imgRef}
        src={src}
        alt={alt ?? 'User avatar'}
        width={width}
        height={height}
        style={style}
        className={className}
        onError={handleError}
        onLoad={handleLoad}
      />
    )
  },
  (prevProps, nextProps) => {
    // Only re-render if src changes
    return prevProps.src === nextProps.src
  },
)
AvatarImage.displayName = 'AvatarImage'

interface NoAssigneeAvatarProps {
  width: number
  height: number
  className?: string
  iconClassName?: string
  filled?: boolean
  tooltip?: string
}

export const NoAssigneeAvatar: React.FC<NoAssigneeAvatarProps> = ({
  width,
  height,
  className,
  iconClassName,
  filled = true,
  tooltip,
}) => {
  return (
    <Tooltip>
      <TooltipTrigger>
        <div
          className={twMerge(
            'flex items-center justify-center rounded-full border border-dashed border-primary-400 dark:border-primary-500 p-[2px]',
            className,
          )}
          style={{ width: `${width}px`, height: `${height}px` }}
        >
          {filled ? (
            <HiUser
              className={twMerge(
                'text-primary-500 dark:text-primary-400',
                iconClassName,
              )}
              style={{ width: `${width * 0.6}px`, height: `${height * 0.6}px` }}
            />
          ) : (
            <HiOutlineUser
              className={twMerge(
                'text-primary-700 dark:text-primary-300',
                iconClassName,
              )}
              style={{ width: `${width * 0.6}px`, height: `${height * 0.6}px` }}
            />
          )}
        </div>
      </TooltipTrigger>
      {tooltip && <TooltipContent>{tooltip}</TooltipContent>}
    </Tooltip>
  )
}

interface UnassignedAvatarProps {
  width: number
  height: number
  align?: 'start' | 'center' | 'end'
  alignOffset?: number
  side?: 'top' | 'right' | 'bottom' | 'left'
  sideOffset?: number
  contentClassName?: string
  triggerClassName?: string
  noTooltip?: boolean
}

export const UnassignedAvatar: React.FC<UnassignedAvatarProps> = memo(
  ({
    width,
    height,
    align = 'center',
    alignOffset = 0,
    side = 'top',
    sideOffset = 5,
    contentClassName,
    triggerClassName,
    noTooltip = false,
  }) => {
    return (
      <Tooltip>
        <TooltipTrigger>
          <NoAssigneeAvatar
            filled={true}
            width={width}
            height={height}
            className={triggerClassName}
          />
        </TooltipTrigger>
        {!noTooltip && (
          <TooltipContent
            align={align}
            alignOffset={alignOffset}
            side={side}
            sideOffset={sideOffset}
            className={twMerge('text-sm select-none', contentClassName)}
          >
            <span>Unassigned</span>
          </TooltipContent>
        )}
      </Tooltip>
    )
  },
  (prevProps, nextProps) => {
    return (
      prevProps.width === nextProps.width &&
      prevProps.height === nextProps.height &&
      prevProps.align === nextProps.align &&
      prevProps.alignOffset === nextProps.alignOffset &&
      prevProps.side === nextProps.side &&
      prevProps.sideOffset === nextProps.sideOffset &&
      prevProps.triggerClassName === nextProps.triggerClassName &&
      prevProps.contentClassName === nextProps.contentClassName
    )
  },
)

const Avatar: React.FC<Avatar> = ({
  src,
  name,
  width,
  height,
  align = 'center',
  alignOffset = 0,
  side = 'top',
  sideOffset = 5,
  noTooltip = false,
  includeLabel = false,
  fallback,
  className,
  triggerClassName,
  labelClassName,
  delayFallback = 0,
  onClick = null,
  style,
  initialShowFallback: _initialShowFallback,
  startWithFallback: _startWithFallback,
}) => {
  const srcIsEmpty = src === undefined || src === null || src === ''
  const startWithFallback =
    _startWithFallback !== undefined ? _startWithFallback : srcIsEmpty

  const [showFallback, setShowFallback] = useState<boolean>(
    _initialShowFallback !== undefined ? _initialShowFallback : srcIsEmpty,
  )
  const [errored, setErrored] = useState<boolean>(false)
  const [loaded, setLoaded] = useState<boolean>(false)

  const initials = getInitials(name)

  // if startWithFallback is not provided, use the src to determine if a fallback should be shown

  const handleError = useCallback(() => {
    setShowFallback(true)
    setErrored(true)
    setLoaded(false)
  }, [setShowFallback, setErrored])

  const handleLoad = useCallback(() => {
    setShowFallback(false)
    setErrored(false)
    setLoaded(true)
  }, [setShowFallback, setErrored])

  useEffect(() => {
    // hacky way of getting fallback to work instead of using radix primitives;
    // radix primitives were causing flashing every time the UI rerendered, which
    // led to a bad UX for avatars that exist in virtualized lists
    setErrored(false)
    setLoaded(false)
    if (delayFallback === 0) {
      setShowFallback(src === undefined || src === null || src === '')
    } else {
      const timeoutId = setTimeout(
        () => {
          setShowFallback(src === undefined || src === null)
        },
        src ? 0 : delayFallback,
      )
      return () => {
        clearTimeout(timeoutId)
      }
    }
  }, [src])

  const triggerClassNames = twMerge(
    'flex items-center gap-x-2',
    onClick !== null ? 'cursor-pointer' : 'cursor-default',
    triggerClassName ?? '',
  )

  const avatarContent = (
    <>
      <RadixAvatar.Root
        style={{ width: `${width}px`, height: `${height}px` }}
        className={twMerge(
          'relative inline-flex select-none items-center justify-center overflow-hidden rounded-full align-middle',
          className,
        )}
      >
        {src && !errored && (
          <AvatarImage
            src={src}
            alt={name}
            width={width}
            height={height}
            onError={handleError}
            onLoad={handleLoad}
          />
        )}
        {(showFallback || (startWithFallback && !loaded)) && (
          <div
            className={twMerge(
              'absolute inset-0 flex h-full w-full rounded-[inherit] items-center justify-center leading-1 text-primary-900 dark:text-primary-100 bg-primary-200 dark:bg-primary-600 text-sm font-medium',
              className,
            )}
            style={style}
          >
            <div
              style={{ fontSize: `${width * 0.45}px` }}
              className={twMerge('text-xs select-none', labelClassName)}
            >
              {fallback ?? initials}
            </div>
          </div>
        )}
      </RadixAvatar.Root>
      {includeLabel && name && (
        <span
          className={twMerge(
            'whitespace-nowrap text-sm text-primary-900 dark:text-primary-100',
            labelClassName,
          )}
        >
          {name}
        </span>
      )}
    </>
  )

  const trigger =
    onClick !== null ? (
      <button type="button" className={triggerClassNames} onClick={onClick}>
        {avatarContent}
      </button>
    ) : (
      <span className={triggerClassNames}>{avatarContent}</span>
    )

  if (noTooltip || !name) {
    return trigger
  }

  return (
    <Tooltip delayDuration={120}>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
      >
        {name}
      </TooltipContent>
    </Tooltip>
  )
}

export const UserAvatar: React.FC<UserAvatarProps> = ({
  userId,
  orgId,
  user: resolvedUser,
  ...rest
}) => {
  const { activeOrgId } = useActiveOrgId()
  const lookupOrgId = resolvedUser ? '' : (orgId ?? activeOrgId)
  const { data: fetchedUser, isLoading } = useOrganizationUser({
    orgId: lookupOrgId,
    userId: userId as UUID,
  })
  const user = resolvedUser ?? fetchedUser
  const name = user ? `${user.first_name} ${user.last_name}` : undefined
  if (!resolvedUser && isLoading) {
    return (
      <AvatarSkeleton
        width={rest.width ?? 28}
        height={rest.height ?? 28}
        noName={!(rest.includeLabel ?? false)}
      />
    )
  }
  return <Avatar src={user?.picture_url} name={name} {...rest} />
}

export const CountAvatar: React.FC<CountAvatarProps> = ({
  count,
  width,
  height,
  className,
  users,
}) => {
  return (
    <Tooltip delayDuration={0}>
      {/* asChild keeps the badge a plain div — the default trigger is a
          <button>, which dialogs can auto-focus and pop the tooltip open. */}
      <TooltipTrigger asChild>
        <div
          className={twMerge(
            'flex items-center justify-center bg-primary-200 dark:bg-primary-700 rounded-full ring-2 ring-white dark:ring-primary-800',
            className,
          )}
          style={{ width: `${width}px`, height: `${height}px` }}
        >
          <span
            style={{ fontSize: `${width * 0.48}px` }}
            className="font-semibold text-primary-700 dark:text-primary-300"
          >
            +{count}
          </span>
        </div>
      </TooltipTrigger>
      {users && users.length > 0 && (
        <TooltipContent inverseColors={true} className="p-2">
          <div className="flex flex-col gap-2">
            {users.map((user) => (
              <UserAvatar
                key={user.id}
                userId={user.id}
                user={user}
                width={width}
                height={height}
                includeLabel={true}
                noTooltip={true}
              />
            ))}
          </div>
        </TooltipContent>
      )}
    </Tooltip>
  )
}

export const Users: React.FC<UsersProps> = ({
  userIds,
  direction = 'ltr',
  width = 28,
  height = 28,
  onClick,
  avatarClassName,
  className,
  animationDisabled = false,
  limit,
  hideOverflowBadge = false,
}) => {
  const handleClick = useCallback(
    (user: User) => () => {
      onClick?.(user)
    },
    [onClick],
  )

  const { activeOrgId } = useActiveOrgId()

  const selectUsers = useCallback(
    (data: InfiniteData<PaginatedResponse<User>>) =>
      data.pages
        .flatMap((page) => page.data)
        .filter((user) => userIds.includes(user.id as UUID)),
    [userIds],
  )

  const { data: users } = useOrganizationUsers({
    orgId: activeOrgId,
    select: selectUsers,
  })

  if (animationDisabled) {
    if (!users) {
      return null
    }
    return (
      <NonAnimatedUsers
        users={users}
        width={width}
        height={height}
        className={className}
        avatarClassName={avatarClassName}
        limit={limit}
        onClick={onClick}
        hideOverflowBadge={hideOverflowBadge}
      />
    )
  }

  // RTL stacking offsets must be based on how many avatars are actually
  // rendered (capped count + overflow badge, when shown), not the full user
  // count, otherwise a `limit` smaller than users.length shifts everything
  // off-screen -- or, when `hideOverflowBadge` suppresses the badge, leaves
  // a stacking gap sized for a slot that never renders.
  // Sorted copy computed once: the visible stack and the "+N" overflow badge
  // must slice the SAME ordering, or the badge lists users already displayed
  // and omits hidden ones. (toSorted, not sort: `users` is cache data.)
  const sortedUsers =
    users && users.toSorted((a, b) => a.first_name.localeCompare(b.first_name))
  const visibleCount = users ? Math.min(limit ?? users.length, users.length) : 0
  const hasOverflow = !!(limit && users && users.length > limit)
  const totalRendered =
    visibleCount + (hasOverflow && !hideOverflowBadge ? 1 : 0)

  return (
    <motion.div
      className={twMerge('flex', className)}
      initial="collapsed"
      whileHover={animationDisabled ? undefined : 'expanded'}
    >
      {sortedUsers &&
        sortedUsers.length > 0 &&
        sortedUsers.slice(0, limit ?? sortedUsers.length).map((u, index) => (
          <motion.div
            key={u.id}
            variants={{
              collapsed: {
                x: `${direction === 'rtl' ? (totalRendered - index - 1) * 4 : index * -4}px`,
              },
              expanded: {
                x: `${direction === 'rtl' ? (totalRendered - index - 1) * -4 : index * 4}px`,
              },
            }}
            transition={{ duration: 0.15, delay: 0.05 }}
            style={{
              zIndex: direction === 'rtl' ? totalRendered - index : index,
            }}
            className={twMerge(
              'rounded-full ring-2 ring-primary-50 dark:ring-primary-800',
              onClick ? 'cursor-pointer hover:scale-110' : 'cursor-default',
              avatarClassName,
            )}
            onClick={handleClick(u)}
          >
            <Avatar
              src={u.picture_url}
              name={`${u.first_name} ${u.last_name}`}
              width={width}
              height={height}
              triggerClassName={twMerge(
                'transition-all duration-150 ease-in-out',
                onClick ? '!cursor-pointer hover:scale-[115%]' : '',
              )}
            />
          </motion.div>
        ))}
      {!hideOverflowBadge && limit && users && users.length > limit && (
        <motion.div
          variants={{
            collapsed: {
              x: `${direction === 'rtl' ? (totalRendered - limit - 1) * 4 : limit * -4}px`,
            },
            expanded: {
              x: `${direction === 'rtl' ? (totalRendered - limit - 1) * -4 : limit * 4}px`,
            },
          }}
          transition={{ duration: 0.15, delay: 0.05 }}
          style={{
            zIndex: direction === 'rtl' ? totalRendered - limit : limit,
          }}
          className={twMerge(
            'rounded-full ring-2 ring-primary-50 dark:ring-primary-800',
          )}
        >
          {/* CountAvatar shows the roster tooltip itself when given users —
              no extra (focusable) tooltip wrapper needed. */}
          <CountAvatar
            users={sortedUsers?.slice(limit)}
            count={users.length - limit}
            width={width}
            height={height}
          />
        </motion.div>
      )}
    </motion.div>
  )
}

interface NonAnimatedUsersProps extends Omit<
  UsersProps,
  'animationDisabled' | 'direction' | 'userIds'
> {
  users: User[]
}

export const NonAnimatedUsers: React.FC<NonAnimatedUsersProps> = ({
  users,
  width = 28,
  height = 28,
  onClick,
  avatarClassName,
  className,
  limit,
  hideOverflowBadge = false,
}) => {
  const handleClick = (user: User) => () => {
    onClick?.(user)
  }
  // Sorted copy computed once: the visible stack and the "+N" overflow badge
  // must slice the SAME ordering. (toSorted: don't mutate the callers' array.)
  const sortedUsers = users.toSorted((a, b) =>
    a.first_name.localeCompare(b.first_name),
  )
  return (
    <div className={twMerge('flex -space-x-2', className)}>
      {sortedUsers.slice(0, limit ?? sortedUsers.length).map((user, idx) => (
        <div
          key={user.id}
          className={twMerge(
            'flex items-center w-fit h-fit gap-x-2 rounded-full ring-2 ring-primary-50 dark:ring-primary-800',
            avatarClassName,
          )}
          style={{
            zIndex: idx,
          }}
        >
          <UserAvatar
            userId={user.id}
            // Callers hand us resolved Users — pass them through so the
            // avatar skips the org-users fetch (which would miss entirely
            // for users outside the active org).
            user={user}
            width={width}
            height={height}
            className={avatarClassName}
            // Only a real handler makes the avatar a <button>. A no-op
            // handler would render focusable buttons whose name tooltips
            // pop open (tooltips open on focus) when a dialog auto-focuses
            // its first tabbable element.
            onClick={onClick ? handleClick(user) : undefined}
          />
        </div>
      ))}
      {!hideOverflowBadge && limit && users.length > limit && (
        <div
          className={twMerge('flex items-center gap-x-2 rounded-full')}
          style={{
            zIndex: users.length,
          }}
        >
          <CountAvatar
            count={users.length - limit}
            users={sortedUsers.slice(limit)}
            width={width}
            height={height}
            className={avatarClassName}
          />
        </div>
      )}
    </div>
  )
}

interface FaviconAvatarProps {
  websiteUrl?: string
  width?: number
  height?: number
  name?: string
}

export const FaviconAvatar: React.FC<FaviconAvatarProps> = memo(
  ({ websiteUrl, width = 32, height = 32, name }) => {
    const { theme } = useTheme()
    const color = useMemo(
      () =>
        randomcolor({
          seed: name,
          luminosity: theme === 'dark' ? 'dark' : 'light',
        }),
      [name, theme],
    )
    const faviconUrl = websiteUrl ? getFaviconUrl(websiteUrl, 128) : undefined
    const fallback = faviconUrl === undefined
    return (
      <Avatar
        src={faviconUrl}
        name={name ?? websiteUrl}
        width={width}
        height={height}
        noTooltip={true}
        style={{ backgroundColor: color }}
        initialShowFallback={fallback}
        startWithFallback={fallback}
      />
    )
  },
)

Avatar.displayName = 'Avatar'
UnassignedAvatar.displayName = 'UnassignedAvatar'
CountAvatar.displayName = 'CountAvatar'
FaviconAvatar.displayName = 'FaviconAvatar'

export default memo(Avatar)
